import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Seo, { articleSchema, breadcrumbSchema, faqSchema } from "@/components/Seo";
import Crumbs from "@/components/Crumbs";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Check,
  Share2,
  Phone,
  RotateCcw,
  LifeBuoy,
} from "lucide-react";

const QUESTIONS = [
  "¿Has consumido pasta base en los últimos 30 días?",
  "¿Sientes la necesidad de consumir cada vez más para obtener el mismo efecto?",
  "¿Has intentado dejar de consumir sin éxito?",
  "¿Dedicas mucho tiempo a conseguir, consumir o recuperarte de la pasta base?",
  "¿Has dejado de hacer actividades que antes disfrutabas por consumir?",
  "¿Sigues consumiendo a pesar de saber que te causa problemas de salud?",
  "¿Has tenido problemas con tu familia, trabajo o estudios por el consumo?",
  "¿Consumes en situaciones peligrosas (solo, de noche, en zonas de riesgo)?",
  "¿Sientes ansiedad, irritabilidad o malestar físico cuando no consumes?",
  "¿Has robado, mentido o hecho cosas que no harías normalmente para conseguir pasta base?",
];

const OPTIONS = [
  { label: "Nunca", value: 0 },
  { label: "A veces", value: 1 },
  { label: "Frecuentemente", value: 2 },
  { label: "Siempre", value: 3 },
];

function getResult(score) {
  if (score <= 7) {
    return {
      level: "Riesgo Bajo",
      tone: "emerald",
      title: "Riesgo Bajo",
      message:
        "Tus respuestas sugieren un riesgo bajo de dependencia. Mantente informado y atento a señales. Si tienes dudas, conversar con un profesional nunca está de más.",
      ctaLabel: "Explorar más información",
    };
  }
  if (score <= 17) {
    return {
      level: "Riesgo Moderado",
      tone: "amber",
      title: "Riesgo Moderado",
      message:
        "Tus respuestas sugieren un riesgo moderado. El consumo podría estar afectando áreas de tu vida. Te recomendamos hablar con un profesional lo antes posible; la intervención temprana tiene mejores resultados.",
      ctaLabel: "Hablar con un Profesional",
    };
  }
  return {
    level: "Riesgo Alto",
    tone: "red",
    title: "Riesgo Alto",
    message:
      "Tus respuestas sugieren un riesgo alto de dependencia. Es importante que busques ayuda profesional lo antes posible. Hay centros especializados y tratamiento gratuito en Chile que pueden ayudarte.",
    ctaLabel: "Buscar Centros Ahora",
  };
}

const faq = [
  {
    question: "¿Este test diagnostica adicción?",
    answer:
      "No. Es un test orientativo basado en criterios clínicos DSM-5 adaptados. No reemplaza un diagnóstico profesional ni constituye atención clínica.",
  },
  {
    question: "¿Mis respuestas quedan guardadas en algún lado?",
    answer:
      "No. El test funciona completamente en tu navegador. No se envía información a ningún servidor, no se guarda en ninguna base de datos.",
  },
  {
    question: "¿Qué hago si el resultado me da riesgo alto?",
    answer:
      "Llama al Fono Drogas SENDA 1412 (24/7, gratuito) o acude al CESFAM de tu comuna para iniciar una evaluación profesional. También puedes encontrar centros y profesionales verificados en la red SinAdicciones.org.",
  },
];

export default function TestDependencia() {
  const [step, setStep] = useState(-1); // -1 = intro, 0..9 = preguntas, 10 = resultado
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));

  const progress = useMemo(() => {
    if (step < 0) return 0;
    if (step >= QUESTIONS.length) return 100;
    return (step / QUESTIONS.length) * 100;
  }, [step]);

  const score = useMemo(
    () => answers.reduce((s, v) => s + (v ?? 0), 0),
    [answers]
  );

  const crumbs = [{ name: "Test de Dependencia", path: "/test-dependencia" }];
  const schemas = [
    articleSchema({
      title: "Test de Dependencia a la Pasta Base - Evaluación Gratuita",
      description:
        "Responde 10 preguntas para evaluar tu nivel de dependencia a la pasta base. Test gratuito, confidencial y basado en criterios clínicos.",
      path: "/test-dependencia",
    }),
    breadcrumbSchema([{ name: "Inicio", path: "/" }, ...crumbs]),
    faqSchema(faq),
    {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "Test de Dependencia a la Pasta Base",
      applicationCategory: "HealthApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "CLP" },
    },
  ];

  function answerAndAdvance(value) {
    const next = [...answers];
    next[step] = value;
    setAnswers(next);
    setTimeout(() => setStep((s) => s + 1), 180);
  }

  function reset() {
    setAnswers(Array(QUESTIONS.length).fill(null));
    setStep(-1);
  }

  async function share() {
    const url = "https://pasta-base.cl/test-dependencia";
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Test de Dependencia a la Pasta Base",
          text: "Evalúa tu nivel de dependencia con este test confidencial y gratuito.",
          url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copiado al portapapeles");
      }
    } catch {
      toast.error("No se pudo compartir");
    }
  }

  const showResult = step >= QUESTIONS.length;
  const result = showResult ? getResult(score) : null;

  return (
    <>
      <Seo
        title="Test de Dependencia a la Pasta Base - Evaluación Gratuita | pasta-base.cl"
        description="Responde 10 preguntas para evaluar tu nivel de dependencia a la pasta base. Test gratuito, confidencial y basado en criterios clínicos. Obtén orientación inmediata."
        path="/test-dependencia"
        schema={schemas}
      />

      <div className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <Crumbs items={crumbs} />

        {step === -1 && (
          <section data-testid="test-intro">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">
              Evaluación clínica orientativa
            </span>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl">
              Test de Dependencia a la Pasta Base
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-300">
              10 preguntas basadas en criterios clínicos DSM-5. Te toma menos de
              3 minutos. Es anónimo, no requiere registro y tus respuestas no
              salen de tu navegador.
            </p>

            <div className="mt-8 flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />
              <div className="text-sm text-slate-300">
                <strong className="block text-amber-200">Importante</strong>
                Este test es orientativo y <strong>NO reemplaza un diagnóstico profesional</strong>.
                Su objetivo es ayudarte a decidir si necesitas buscar ayuda.
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button
                data-testid="start-test-btn"
                onClick={() => setStep(0)}
                className="btn-press rounded-full bg-emerald-500 px-6 py-6 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
              >
                Comenzar el test
                <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Link
                to="/preguntas-frecuentes"
                className="rounded-full border border-slate-800 bg-slate-900 px-5 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800"
              >
                Ver preguntas frecuentes
              </Link>
            </div>
          </section>
        )}

        {step >= 0 && !showResult && (
          <section data-testid="test-wizard">
            <div className="mb-8 flex items-center justify-between text-xs uppercase tracking-wider text-slate-400">
              <span>
                Pregunta <span className="text-emerald-300">{step + 1}</span> de{" "}
                {QUESTIONS.length}
              </span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5 bg-slate-800" />

            <div key={step} className="mt-10 fade-up">
              <h2 className="text-2xl font-bold leading-tight text-slate-50 sm:text-3xl">
                {QUESTIONS[step]}
              </h2>
              <div className="mt-8 grid gap-3">
                {OPTIONS.map((opt) => {
                  const selected = answers[step] === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`test-opt-${opt.value}`}
                      onClick={() => answerAndAdvance(opt.value)}
                      className={`btn-press flex items-center justify-between rounded-xl border px-5 py-4 text-left text-base font-medium transition ${
                        selected
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                          : "border-slate-800 bg-slate-900/60 text-slate-100 hover:border-slate-700 hover:bg-slate-900"
                      }`}
                    >
                      <span>{opt.label}</span>
                      {selected && <Check className="h-4 w-4" />}
                    </button>
                  );
                })}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button
                  type="button"
                  data-testid="test-back-btn"
                  onClick={() => setStep((s) => Math.max(-1, s - 1))}
                  className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Anterior
                </button>
                <button
                  type="button"
                  data-testid="test-reset-btn"
                  onClick={reset}
                  className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reiniciar
                </button>
              </div>
            </div>
          </section>
        )}

        {showResult && result && (
          <section data-testid="test-result" className="fade-up">
            <div className="mb-8 flex items-center justify-between text-xs uppercase tracking-wider text-slate-400">
              <span>Resultado</span>
              <span>Puntaje: {score} / 30</span>
            </div>

            <div
              className={`rounded-3xl border p-8 ${
                result.tone === "emerald"
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : result.tone === "amber"
                  ? "border-amber-500/30 bg-amber-500/5"
                  : "border-red-500/30 bg-red-500/5"
              }`}
            >
              <span
                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${
                  result.tone === "emerald"
                    ? "bg-emerald-500/20 text-emerald-300"
                    : result.tone === "amber"
                    ? "bg-amber-500/20 text-amber-300"
                    : "bg-red-500/20 text-red-300"
                }`}
              >
                {result.level}
              </span>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
                {result.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-200">
                {result.message}
              </p>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <a
                  href="https://sinadicciones.org/centros-de-rehabilitacion-drogas-chile"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="result-centros-btn"
                  className="btn-press inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-3 text-sm font-semibold text-white hover:bg-red-600"
                >
                  <LifeBuoy className="h-4 w-4" />
                  Buscar Centros Especializados
                </a>
                <a
                  href="https://sinadicciones.org/profesionales"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="result-profesionales-btn"
                  className="btn-press inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-emerald-400"
                >
                  Hablar con un Profesional
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-800/80 pt-6">
                <a
                  href="tel:1412"
                  className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-300"
                >
                  <Phone className="h-4 w-4 text-red-400" />
                  Fono Drogas SENDA:{" "}
                  <span className="font-semibold text-slate-100">1412</span>
                </a>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="share-test-btn"
                    onClick={share}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Compartir
                  </button>
                  <button
                    type="button"
                    data-testid="redo-test-btn"
                    onClick={reset}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Rehacer el test
                  </button>
                </div>
              </div>
            </div>

            <p className="mt-8 text-center text-xs text-slate-500">
              Este test es orientativo y no reemplaza un diagnóstico profesional.
            </p>
          </section>
        )}
      </div>
    </>
  );
}
